import { useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
import maplibregl from "maplibre-gl";
import "leaflet/dist/leaflet.css";
import "maplibre-gl/dist/maplibre-gl.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { useTheme } from "../../contexts/theme";
import { MdMyLocation, MdFullscreen, MdFullscreenExit } from "react-icons/md";
import {
   round6,
   haversineDistance,
   nearestBorderPoint,
   buildCircleGeoJSON,
   buildGuideLinesGeoJSON,
   normalizeCoordinates,
} from "../../utils/geo";
import "./Map.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
   iconRetinaUrl: markerIcon2x,
   iconUrl: markerIcon,
   shadowUrl: markerShadow,
});

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Map component
 *
 * Props:
 *  - coordinates           { latitude, longitude } | Array<{ latitude, longitude }>
 *                          — one or more marker positions; each gets its own circle
 *  - radius                number (meters) — shared circle radius for all markers
 *  - draggable             bool (default false) — enables drag on the FIRST marker
 *  - showRadiusControl     bool (default false) — renders radius input overlay (top-right)
 *  - showCoordinateControls bool (default false) — renders lat/lng inputs for the FIRST marker
 *  - onCenterChange        ({ latitude, longitude }) — fired when first marker moves
 *  - onRadiusChange        (number) — fired when radius input changes
 *  - readOnly              bool (default false) — disables all input editing
 *  - useLeaflet            bool (default false) — use Leaflet instead of MapLibre
 *  - trackUserPosition     bool (default false) — watch browser geolocation and render a
 *                          secondary marker (green = inside at least one radius, red = outside
 *                          all). When outside all, draws dashed guide lines to each circle border.
 */
export function Map({
   useLeaflet = false,
   coordinates,
   radius,
   draggable = false,
   showRadiusControl = false,
   showCoordinateControls = false,
   onCenterChange,
   onRadiusChange,
   readOnly = false,
   trackUserPosition = false,
   onUserInsideAny,
}) {
   const [useMapLibre, setUseMapLibre] = useState(!useLeaflet);
   const [isFullscreen, setIsFullscreen] = useState(false);
   const { themeName } = useTheme();
   const mapContainerRef = useRef(null);

   // Leaflet refs
   const leafletMapRef = useRef(null);
   const leafletMarkersRef = useRef([]); // Array<L.Marker>

   // MapLibre refs
   const maplibreMapRef = useRef(null);
   const maplibreMarkersRef = useRef([]); // Array<maplibregl.Marker>
   const currentStyleRef = useRef(null);

   // Always-current refs for closures
   const radiusRef = useRef(radius);
   useEffect(() => { radiusRef.current = radius; }, [radius]);
   const coordinatesRef = useRef(coordinates);
   useEffect(() => { coordinatesRef.current = coordinates; }, [coordinates]);

   // ─── User position tracking ────────────────────────────────────────────────
   const [userPosition, setUserPosition] = useState(null);
   const [userInsideAny, setUserInsideAny] = useState(null); // true | false | null
   const [distanceToNearest, setDistanceToNearest] = useState(null); // meters, when outside all
   const userMarkerRef = useRef(null);
   const watchIdRef = useRef(null);

   // ─── Coordinate inputs (first marker only) ─────────────────────────────────
   const firstCoord = normalizeCoordinates(coordinates)[0] ?? null;
   const [coordLat, setCoordLat] = useState(
      firstCoord?.latitude != null ? round6(firstCoord.latitude) : ""
   );
   const [coordLng, setCoordLng] = useState(
      firstCoord?.longitude != null ? round6(firstCoord.longitude) : ""
   );
   const isEditingCoordsRef = useRef(false);
   const setCoordLatRef = useRef(setCoordLat);
   const setCoordLngRef = useRef(setCoordLng);

   useEffect(() => setUseMapLibre(!useLeaflet), [useLeaflet]);

   // Sync coord inputs when first marker changes externally
   useEffect(() => {
      if (isEditingCoordsRef.current) return;
      const fc = normalizeCoordinates(coordinatesRef.current)[0];
      if (!fc) return;
      setCoordLat(round6(fc.latitude));
      setCoordLng(round6(fc.longitude));
   }, [coordinates]);

   // ─── MapLibre: idempotent circle layers per marker ─────────────────────────
   function circleSourceId(i) { return `radius-circle-${i}`; }
   function circleFillId(i) { return `radius-circle-fill-${i}`; }
   function circleBorderId(i) { return `radius-circle-border-${i}`; }

   function addCircleLayer(map, i, lng, lat, radiusM) {
      const srcId = circleSourceId(i);
      if (map.getSource(srcId)) return;
      map.addSource(srcId, {
         type: "geojson",
         data: buildCircleGeoJSON(lng, lat, radiusM || 0),
      });
      map.addLayer({
         id: circleFillId(i),
         type: "fill",
         source: srcId,
         paint: { "fill-color": "#3b82f6", "fill-opacity": 0.15 },
      });
      map.addLayer({
         id: circleBorderId(i),
         type: "line",
         source: srcId,
         paint: { "line-color": "#3b82f6", "line-width": 2, "line-opacity": 0.7 },
      });
   }

   function addAllCircleLayers(map, coordsList, radiusM) {
      coordsList.forEach(({ latitude, longitude }, i) => {
         addCircleLayer(map, i, longitude, latitude, radiusM);
      });
   }

   // ─── Guide lines (user → circle borders when outside all) ─────────────────
   const GUIDE_SRC = "guide-lines";
   const GUIDE_LAYER = "guide-lines-layer";

   function ensureGuideLayer(map) {
      if (map.getSource(GUIDE_SRC)) return;
      map.addSource(GUIDE_SRC, {
         type: "geojson",
         data: buildGuideLinesGeoJSON([]),
      });
      map.addLayer({
         id: GUIDE_LAYER,
         type: "line",
         source: GUIDE_SRC,
         paint: {
            "line-color": "#ef4444",
            "line-width": 2,
            "line-opacity": 0.75,
            "line-dasharray": [4, 4],
         },
      });
   }

   function updateGuideLines(map, userLat, userLng, coordsList, radiusM) {
      ensureGuideLayer(map);
      const src = map.getSource(GUIDE_SRC);
      if (!src) return;

      let lines = [];
      if (radiusM && radiusM > 0 && userLat != null && userLng != null) {
         lines = coordsList
            .filter(({ latitude, longitude }) => {
               const dist = haversineDistance(userLat, userLng, latitude, longitude);
               return dist > radiusM; // only draw for circles where user is outside
            })
            .map(({ latitude, longitude }) => {
               const border = nearestBorderPoint(userLat, userLng, latitude, longitude, radiusM);
               return [
                  [userLng, userLat],
                  [border.lng, border.lat],
               ];
            });
      }

      src.setData(buildGuideLinesGeoJSON(lines));
   }

   // ─── Update user marker color & guide lines ────────────────────────────────
   function getUserMarkerColor(inside) {
      if (inside === null) return "#6366f1";
      return inside ? "#22c55e" : "#ef4444";
   }

   function createUserMarkerEl(color) {
      // Wrapper keeps label + dot together as a single marker element
      const wrapper = document.createElement("div");
      wrapper.style.cssText = "display:flex;flex-direction:column;align-items:center;gap:3px;pointer-events:none;";

      const label = document.createElement("span");
      label.className = "map-user-marker-label";
      label.textContent = "Você";

      const dot = document.createElement("div");
      dot.className = "map-user-marker-dot";
      dot.style.cssText = `
         width:16px;height:16px;
         background:${color};
         border:2.5px solid #fff;
         border-radius:50%;
         box-shadow:0 0 0 2px ${color},0 2px 6px rgba(0,0,0,.4);
         transition:background 0.4s,box-shadow 0.4s;
      `;

      wrapper.appendChild(label);
      wrapper.appendChild(dot);
      return wrapper;
   }

   const updateUserMarker = useCallback((lat, lng, inside) => {
      const map = maplibreMapRef.current;
      if (!map) return;
      const color = getUserMarkerColor(inside);

      if (userMarkerRef.current) {
         userMarkerRef.current.setLngLat([lng, lat]);
         const wrapper = userMarkerRef.current.getElement();
         const dot = wrapper.querySelector(".map-user-marker-dot");
         if (dot) {
            dot.style.background = color;
            dot.style.boxShadow = `0 0 0 2px ${color},0 2px 6px rgba(0,0,0,.4)`;
         }
      } else {
         const wrapper = createUserMarkerEl(color);
         // Anchor at the center of the dot (offset: label height ≈ 20px + gap 3px)
         userMarkerRef.current = new maplibregl.Marker({
            element: wrapper,
            draggable: false,
            anchor: "bottom",
         })
            .setLngLat([lng, lat])
            .addTo(map);
      }
   }, []);

   // ─── Recompute inside/outside + guide lines whenever relevant state changes
   useEffect(() => {
      if (!userPosition) return;
      const coordsList = normalizeCoordinates(coordinatesRef.current);
      const r = radiusRef.current;

      let inside = null;
      let minDistToBorder = null;

      if (coordsList.length > 0 && r && r > 0) {
         const distances = coordsList.map(({ latitude, longitude }) =>
            haversineDistance(userPosition.latitude, userPosition.longitude, latitude, longitude)
         );
         inside = distances.some((d) => d <= r);

         if (!inside) {
            // Distance to nearest border = distance to nearest center minus radius
            const minToCenterDist = Math.min(...distances);
            minDistToBorder = Math.max(0, Math.round(minToCenterDist - r));
         }
      }

      if (onUserInsideAny) onUserInsideAny(inside);
      setUserInsideAny(inside);
      setDistanceToNearest(inside ? null : minDistToBorder);
      updateUserMarker(userPosition.latitude, userPosition.longitude, inside);

      // Draw/clear guide lines
      const map = maplibreMapRef.current;
      if (map && map.isStyleLoaded()) {
         updateGuideLines(map, userPosition.latitude, userPosition.longitude, coordsList, r);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [userPosition, radius, coordinates]);

   // ─── Start/stop watchPosition ──────────────────────────────────────────────
   useEffect(() => {
      if (!trackUserPosition || !navigator.geolocation) return;

      watchIdRef.current = navigator.geolocation.watchPosition(
         (pos) => setUserPosition({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
         () => { },
         { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
      );

      return () => {
         if (watchIdRef.current != null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
         }
         if (userMarkerRef.current) {
            userMarkerRef.current.remove();
            userMarkerRef.current = null;
         }
      };
   }, [trackUserPosition]);

   // ─── First marker: drag + coord inputs ────────────────────────────────────
   function applyNewCenter(lat, lng) {
      const markers = maplibreMarkersRef.current;
      if (markers[0]) markers[0].setLngLat([lng, lat]);

      const map = maplibreMapRef.current;
      if (map) {
         map.setCenter([lng, lat]);
         const src = map.getSource(circleSourceId(0));
         if (src) src.setData(buildCircleGeoJSON(lng, lat, radiusRef.current || 0));
      }
      onCenterChange?.({ latitude: lat, longitude: lng });
   }

   function handleCoordChange(field, value) {
      if (field === "lat") setCoordLat(value);
      else setCoordLng(value);

      const lat = field === "lat" ? parseFloat(value) : parseFloat(coordLat);
      const lng = field === "lng" ? parseFloat(value) : parseFloat(coordLng);

      if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
         applyNewCenter(lat, lng);
      }
   }

   // ─── Effect 1: Initialize map ──────────────────────────────────────────────
   useEffect(() => {
      const coordsList = normalizeCoordinates(coordinates);
      if (!coordsList.length || !mapContainerRef.current) return;
      const first = coordsList[0];

      if (useMapLibre) {
         // Teardown Leaflet if switching
         if (leafletMapRef.current) {
            leafletMapRef.current.remove();
            leafletMapRef.current = null;
            leafletMarkersRef.current = [];
         }

         if (!maplibreMapRef.current) {
            const style = themeName.name === "classic" ? "/style-light.json" : "/style-dark.json";
            currentStyleRef.current = style;

            const map = new maplibregl.Map({
               container: mapContainerRef.current,
               style,
               center: [first.longitude, first.latitude],
               zoom: 14,
               scrollZoom: true,
            });

            map.addControl(new maplibregl.NavigationControl(), "top-left");

            // Create one marker per coordinate
            const mlMarkers = coordsList.map(({ latitude, longitude }, i) => {
               const el = document.createElement("div");
               el.style.cssText =
                  "width:14px;height:14px;background:#3b82f6;border:2px solid #fff;border-radius:50%;box-shadow:0 0 0 2px #3b82f6;cursor:" +
                  (draggable && i === 0 ? "grab" : "default");

               const mk = new maplibregl.Marker({ element: el, draggable: draggable && i === 0 })
                  .setLngLat([longitude, latitude])
                  .addTo(map);

               if (draggable && i === 0) {
                  mk.on("dragend", () => {
                     const ll = mk.getLngLat();
                     const lat = round6(ll.lat);
                     const lng = round6(ll.lng);
                     setCoordLatRef.current(lat);
                     setCoordLngRef.current(lng);
                     const src = maplibreMapRef.current?.getSource(circleSourceId(0));
                     if (src) src.setData(buildCircleGeoJSON(ll.lng, ll.lat, radiusRef.current || 0));
                     onCenterChange?.({ latitude: ll.lat, longitude: ll.lng });
                  });
               }

               return mk;
            });

            maplibreMarkersRef.current = mlMarkers;

            map.on("load", () => {
               addAllCircleLayers(map, coordsList, radiusRef.current);
               ensureGuideLayer(map);
            });

            maplibreMapRef.current = map;
         }
      } else {
         // Teardown MapLibre if switching
         if (maplibreMapRef.current) {
            maplibreMapRef.current.remove();
            maplibreMapRef.current = null;
            maplibreMarkersRef.current = [];
            currentStyleRef.current = null;
         }

         if (!leafletMapRef.current) {
            const map = L.map(mapContainerRef.current, { scrollWheelZoom: true }).setView(
               [first.latitude, first.longitude], 13
            );
            L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
               attribution:
                  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            }).addTo(map);

            const lfMarkers = coordsList.map(({ latitude, longitude }) =>
               L.marker([latitude, longitude]).addTo(map)
            );

            leafletMapRef.current = map;
            leafletMarkersRef.current = lfMarkers;
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [useMapLibre, normalizeCoordinates(coordinates).length === 0]);

   // ─── Effect 2: Sync markers when coordinates prop changes ─────────────────
   useEffect(() => {
      const coordsList = normalizeCoordinates(coordinates);
      if (!coordsList.length) return;
      const first = coordsList[0];

      if (useMapLibre && maplibreMapRef.current) {
         maplibreMapRef.current.setCenter([first.longitude, first.latitude]);
         const markers = maplibreMarkersRef.current;
         coordsList.forEach(({ latitude, longitude }, i) => {
            if (markers[i]) markers[i].setLngLat([longitude, latitude]);
         });
      } else if (!useMapLibre && leafletMapRef.current) {
         leafletMapRef.current.setView([first.latitude, first.longitude], 13);
         const markers = leafletMarkersRef.current;
         coordsList.forEach(({ latitude, longitude }, i) => {
            if (markers[i]) markers[i].setLatLng([latitude, longitude]);
         });
      }
   }, [coordinates, useMapLibre]);

   // ─── Effect 3: Theme change — reapply style + re-add all layers ────────────
   useEffect(() => {
      if (!useMapLibre || !maplibreMapRef.current) return;
      const style = themeName.name === "classic" ? "/style-light.json" : "/style-dark.json";
      if (currentStyleRef.current === style) return;
      currentStyleRef.current = style;

      const map = maplibreMapRef.current;
      map.setStyle(style);
      map.once("styledata", () => {
         const coordsList = normalizeCoordinates(coordinatesRef.current);
         addAllCircleLayers(map, coordsList, radiusRef.current);
         ensureGuideLayer(map);
      });
   }, [themeName, useMapLibre]);

   // ─── Effect 4: Update circles when radius or coordinates change ────────────
   useEffect(() => {
      if (!maplibreMapRef.current) return;
      const map = maplibreMapRef.current;
      const coordsList = normalizeCoordinates(coordinates);

      const update = () => {
         coordsList.forEach(({ latitude, longitude }, i) => {
            const src = map.getSource(circleSourceId(i));
            if (src) src.setData(buildCircleGeoJSON(longitude, latitude, radius || 0));
         });
         // Also refresh guide lines if tracking
         if (userPosition) {
            updateGuideLines(map, userPosition.latitude, userPosition.longitude, coordsList, radius);
         }
      };

      if (map.isStyleLoaded()) update();
      else map.once("styledata", update);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [radius, coordinates]);

   // ─── Cleanup on unmount ────────────────────────────────────────────────────
   useEffect(() => {
      return () => {
         if (watchIdRef.current != null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
         }
         leafletMapRef.current?.remove();
         maplibreMapRef.current?.remove();
      };
   }, []);

   // ─── Badge state ───────────────────────────────────────────────────────────
   const userStatusLabel =
      userInsideAny === true
         ? "Dentro da área"
         : userInsideAny === false
            ? "Fora da área"
            : userPosition
               ? "Posição obtida"
               : "Obtendo posição...";

   const distanceLabel =
      distanceToNearest != null
         ? distanceToNearest >= 1000
            ? `${(distanceToNearest / 1000).toFixed(1)} km do raio`
            : `${distanceToNearest} m do raio`
         : null;

   const userStatusColor =
      userInsideAny === true ? "#22c55e" : userInsideAny === false ? "#ef4444" : "#6366f1";

   return (
      <div className={`map-root${isFullscreen ? " map-root--fullscreen" : ""}`}>
         {/* Fullscreen toggle button — top-right corner */}
         <button
            className="map-fullscreen-btn"
            onClick={() => setIsFullscreen((v) => !v)}
            title={isFullscreen ? "Sair da tela cheia" : "Expandir para tela cheia"}
         >
            {isFullscreen ? <MdFullscreenExit size={20} /> : <MdFullscreen size={20} />}
         </button>

         {/* Radius input — top-right overlay */}
         {showRadiusControl && (
            <div className="map-radius-overlay">
               <MdMyLocation size={16} className="map-radius-icon" />
               <input
                  id="map-radius-input"
                  type="number"
                  min="10"
                  max="50000"
                  step="10"
                  value={radius ?? ""}
                  onChange={({ target }) =>
                     onRadiusChange?.(target.value ? parseInt(target.value, 10) : null)
                  }
                  placeholder="Raio"
                  disabled={readOnly}
                  className="map-radius-input"
               />
               <span className="map-radius-unit">m</span>
            </div>
         )}

         {/* User position status badge — bottom-left */}
         {trackUserPosition && (
            <div
               className="map-user-position-badge"
               style={{ borderColor: userStatusColor, color: userStatusColor }}
            >
               <span className="map-user-position-dot" style={{ background: userStatusColor }} />
               <span>{userStatusLabel}</span>
               {distanceLabel && (
                  <span className="map-user-distance-label">{distanceLabel}</span>
               )}
            </div>
         )}

         {/* Coordinate inputs (first marker) — bottom overlay */}
         {showCoordinateControls && (
            <div className="map-coord-overlay">
               <div className="map-coord-field">
                  <label className="map-coord-label" htmlFor="map-coord-lat">Lat</label>
                  <input
                     id="map-coord-lat"
                     type="number"
                     step="0.000001"
                     min="-90"
                     max="90"
                     value={coordLat}
                     disabled={readOnly}
                     className="map-coord-input"
                     onFocus={() => { isEditingCoordsRef.current = true; }}
                     onBlur={() => { isEditingCoordsRef.current = false; }}
                     onChange={({ target }) => handleCoordChange("lat", target.value)}
                     placeholder="Latitude"
                  />
               </div>
               <div className="map-coord-separator" />
               <div className="map-coord-field">
                  <label className="map-coord-label" htmlFor="map-coord-lng">Lng</label>
                  <input
                     id="map-coord-lng"
                     type="number"
                     step="0.000001"
                     min="-180"
                     max="180"
                     value={coordLng}
                     disabled={readOnly}
                     className="map-coord-input"
                     onFocus={() => { isEditingCoordsRef.current = true; }}
                     onBlur={() => { isEditingCoordsRef.current = false; }}
                     onChange={({ target }) => handleCoordChange("lng", target.value)}
                     placeholder="Longitude"
                  />
               </div>
            </div>
         )}

         <div ref={mapContainerRef} className="map-canvas" />
      </div>
   );
}